import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import {
    PARTY_HALL_QUOTES_LIST_PATH,
    createPartyHallQuote,
    fetchPartyHallQuoteById,
    fetchPartyHallsByRestaurant,
    isTerminalPartyHallQuoteStatus,
    updatePartyHallQuote,
} from './partyHallQuoteApi';
import { getLocalDateString } from './dailyMenuApi';
import { getActiveRestaurantId } from './entityContext';
import { extractRestaurantId, extractRestaurantName, resolveInitialRestaurant } from './restaurantApi';
import { PARTY_HALL_TYPE_LABELS } from './restaurantServiceConstants';
import { formatMobileDisplay, sanitizeMobileInput, validateMobileNumber } from './formatDisplay';
import { confirmAction } from './ui/feedback';
import './css/Menu.css';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';
import './css/cateringQuoteCreate.css';

const QUOTE_STATUS_EDIT_OPTIONS = ['REQUESTED', 'UNDER_REVIEW', 'REJECTED'];

function getActorId() {
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        return Number(user?.userId ?? user?.User_Id ?? user?.user_id ?? 0);
    } catch (e) {
        return 0;
    }
}

function getLoggedInRestaurantId() {
    const stored = getActiveRestaurantId();
    if (stored && stored !== 'null' && stored !== 'undefined') {
        const id = Number(stored);
        if (Number.isFinite(id) && id > 0) return id;
    }
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        const fromUser = Number(
            user?.restaurantId ?? user?.Restaurant_Id ?? user?.restaurant_id ?? 0
        );
        if (Number.isFinite(fromUser) && fromUser > 0) return fromUser;
    } catch (e) {
        // ignore
    }
    return null;
}

function formatHallLabel(hall) {
    if (!hall) return '';
    const typeLabel =
        PARTY_HALL_TYPE_LABELS[String(hall.hallType || '').toUpperCase()] || hall.hallType || '';
    const name = hall.hallName || `Hall #${hall.partyHallId}`;
    return typeLabel ? `${name} (${typeLabel})` : name;
}

function normalizeAmenityList(raw) {
    if (Array.isArray(raw)) {
        return raw
            .map((item) => String(item ?? '').trim())
            .filter(Boolean);
    }
    if (typeof raw === 'string') {
        const text = raw.trim();
        if (!text) return [];
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) return normalizeAmenityList(parsed);
        } catch (e) {
            // comma-separated fallback
        }
        return text
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);
    }
    return [];
}

function formatEventTimeForInput(raw) {
    if (!raw) return '';
    const s = String(raw).trim();
    if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
    return '';
}

function splitAmenityNotes(amenityNotes, hallAmenities) {
    const parts = normalizeAmenityList(amenityNotes);
    const hallSet = new Set(hallAmenities);
    const selected = parts.filter((part) => hallSet.has(part));
    const extra = parts.filter((part) => !hallSet.has(part)).join(', ');
    return { selected, extra };
}

const CreatePartyHallQuote = () => {
    const navigate = useNavigate();
    const { quoteId: quoteIdParam } = useParams();
    const editQuoteId = Number(quoteIdParam);
    const isEditing = Number.isFinite(editQuoteId) && editQuoteId > 0;
    const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
        useRestaurantEnabledServices();
    const [restaurantId, setRestaurantId] = useState(() => getLoggedInRestaurantId());
    const [resolvingRestaurant, setResolvingRestaurant] = useState(() => !getLoggedInRestaurantId());
    const actorId = getActorId();

    const [halls, setHalls] = useState([]);
    const [loadingHalls, setLoadingHalls] = useState(true);
    const [loadingQuote, setLoadingQuote] = useState(isEditing);
    const [saving, setSaving] = useState(false);
    const [amenitiesHydrated, setAmenitiesHydrated] = useState(false);

    const [partyHallId, setPartyHallId] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerContact, setCustomerContact] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [eventType, setEventType] = useState('');
    const [numberOfGuests, setNumberOfGuests] = useState('');
    const [estimatedTotal, setEstimatedTotal] = useState('');
    const [quotedTotal, setQuotedTotal] = useState('');
    const [quoteStatus, setQuoteStatus] = useState('REQUESTED');
    const [selectedAmenities, setSelectedAmenities] = useState([]);
    const [amenityNotes, setAmenityNotes] = useState('');
    const [specialRequests, setSpecialRequests] = useState('');

    const minEventDate = useMemo(() => getLocalDateString(), []);

    useEffect(() => {
        let cancelled = false;

        const applyRestaurant = (id) => {
            if (cancelled || !id) return;
            setRestaurantId(Number(id));
            setResolvingRestaurant(false);
        };

        const immediate = getLoggedInRestaurantId();
        if (immediate) {
            applyRestaurant(immediate);
            return undefined;
        }

        const resolve = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user') || 'null');
                const userId = user?.userId ?? user?.User_Id ?? user?.user_id;
                if (!userId) {
                    if (!cancelled) setResolvingRestaurant(false);
                    return;
                }
                const initial = await resolveInitialRestaurant(userId);
                const id = extractRestaurantId(initial);
                const name = extractRestaurantName(initial);
                if (id) {
                    localStorage.setItem('restaurantId', String(id));
                    if (name) localStorage.setItem('restaurantName', name);
                    applyRestaurant(id);
                    return;
                }
            } catch (e) {
                // ignore
            }
            if (!cancelled) setResolvingRestaurant(false);
        };

        resolve();
        return () => {
            cancelled = true;
        };
    }, []);

    const loadHalls = useCallback(async () => {
        if (!restaurantId) {
            setLoadingHalls(false);
            setHalls([]);
            return;
        }
        setLoadingHalls(true);
        try {
            const rows = await fetchPartyHallsByRestaurant(restaurantId);
            const normalized = (rows ?? []).map((hall) => ({
                ...hall,
                amenities: normalizeAmenityList(hall.amenities),
            }));
            const active = normalized.filter(
                (hall) => String(hall.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
            );
            setHalls(active);
            if (!isEditing && active.length === 1) {
                setPartyHallId(String(active[0].partyHallId));
                setSelectedAmenities([]);
                if (active[0].basePrice != null && active[0].basePrice !== '') {
                    setEstimatedTotal(String(active[0].basePrice));
                }
            }
        } catch (err) {
            window.alert(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to load party halls'
            );
            setHalls([]);
        } finally {
            setLoadingHalls(false);
        }
    }, [restaurantId, isEditing]);

    useEffect(() => {
        loadHalls();
    }, [loadHalls]);

    useEffect(() => {
        if (!isEditing || !restaurantId || !partyHallId) return;
        if (halls.some((hall) => String(hall.partyHallId) === String(partyHallId))) return;

        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchPartyHallsByRestaurant(restaurantId);
                if (cancelled) return;
                const missing = (rows ?? [])
                    .map((hall) => ({
                        ...hall,
                        amenities: normalizeAmenityList(hall.amenities),
                    }))
                    .find((hall) => String(hall.partyHallId) === String(partyHallId));
                if (missing) {
                    setHalls((current) =>
                        current.some((hall) => String(hall.partyHallId) === String(partyHallId))
                            ? current
                            : [...current, missing]
                    );
                }
            } catch (e) {
                // ignore — form still works with active halls
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEditing, restaurantId, partyHallId, halls]);

    useEffect(() => {
        if (!isEditing) {
            setLoadingQuote(false);
            setAmenitiesHydrated(false);
            return undefined;
        }

        let cancelled = false;
        const loadQuote = async () => {
            setLoadingQuote(true);
            setAmenitiesHydrated(false);
            try {
                const quote = await fetchPartyHallQuoteById(editQuoteId);
                if (cancelled) return;
                if (!quote) {
                    window.alert('Quote not found.');
                    navigate(PARTY_HALL_QUOTES_LIST_PATH);
                    return;
                }
                if (isTerminalPartyHallQuoteStatus(quote.quoteStatus)) {
                    window.alert('This quote can no longer be edited.');
                    navigate(`/party-hall-quote/${editQuoteId}`);
                    return;
                }
                if (quote.restaurantId) {
                    setRestaurantId(Number(quote.restaurantId));
                }
                setPartyHallId(quote.partyHallId != null ? String(quote.partyHallId) : '');
                setCustomerName(quote.customerName || '');
                setCustomerContact(sanitizeMobileInput(quote.customerPhone || ''));
                setEventDate(quote.eventDate ? String(quote.eventDate).slice(0, 10) : '');
                setEventTime(formatEventTimeForInput(quote.eventTime));
                setEventType(quote.eventType || '');
                setNumberOfGuests(
                    quote.numberOfGuests != null ? String(quote.numberOfGuests) : ''
                );
                setEstimatedTotal(
                    quote.estimatedTotal != null ? String(quote.estimatedTotal) : ''
                );
                setQuotedTotal(quote.quotedTotal != null ? String(quote.quotedTotal) : '');
                setQuoteStatus(
                    quote.quoteStatus === 'CONFIRMED'
                        ? 'UNDER_REVIEW'
                        : quote.quoteStatus || 'REQUESTED'
                );
                setSpecialRequests(quote.specialRequests || '');
                setSelectedAmenities([]);
                setAmenityNotes(quote.amenityNotes || '');
            } catch (err) {
                if (!cancelled) {
                    window.alert(
                        err?.response?.data?.error ||
                            err?.response?.data?.message ||
                            err.message ||
                            'Failed to load quote'
                    );
                    navigate(PARTY_HALL_QUOTES_LIST_PATH);
                }
            } finally {
                if (!cancelled) setLoadingQuote(false);
            }
        };

        loadQuote();
        return () => {
            cancelled = true;
        };
    }, [editQuoteId, isEditing, navigate]);

    const selectedHall = useMemo(
        () => halls.find((hall) => String(hall.partyHallId) === String(partyHallId)) || null,
        [halls, partyHallId]
    );

    const hallAmenities = useMemo(
        () => normalizeAmenityList(selectedHall?.amenities),
        [selectedHall]
    );

    useEffect(() => {
        if (!isEditing || loadingQuote || amenitiesHydrated || !selectedHall) return;
        const { selected, extra } = splitAmenityNotes(amenityNotes, hallAmenities);
        setSelectedAmenities(selected);
        setAmenityNotes(extra);
        setAmenitiesHydrated(true);
    }, [
        isEditing,
        loadingQuote,
        amenitiesHydrated,
        selectedHall,
        hallAmenities,
        amenityNotes,
    ]);

    const handleHallChange = (value) => {
        setPartyHallId(value);
        setSelectedAmenities([]);
        const hall = halls.find((row) => String(row.partyHallId) === String(value));
        if (hall?.basePrice != null && hall.basePrice !== '') {
            setEstimatedTotal(String(hall.basePrice));
        }
    };

    const toggleAmenity = (amenity) => {
        setSelectedAmenities((prev) =>
            prev.includes(amenity)
                ? prev.filter((item) => item !== amenity)
                : [...prev, amenity]
        );
    };

    const buildValidatedPayload = () => {
        if (!restaurantId) {
            window.alert('Restaurant not found.');
            return null;
        }
        if (!actorId) {
            window.alert('Signed-in user not found.');
            return null;
        }
        if (!String(customerName || '').trim()) {
            window.alert('Customer name is required.');
            return null;
        }
        const phoneError = validateMobileNumber(customerContact, { label: 'Phone number' });
        if (phoneError) {
            window.alert(phoneError);
            return null;
        }
        if (!eventDate) {
            window.alert('Event date is required.');
            return null;
        }
        if (!partyHallId && halls.length > 1) {
            window.alert('Select a party hall.');
            return null;
        }
        if (halls.length === 0) {
            window.alert('No active party hall is configured for this restaurant.');
            return null;
        }

        const guestsRaw = String(numberOfGuests || '').trim();
        if (!guestsRaw) {
            window.alert('Capacity is required.');
            return null;
        }
        const guests = Number(guestsRaw);
        if (!Number.isFinite(guests) || guests <= 0) {
            window.alert('Capacity must be a positive number.');
            return null;
        }
        const minCapacity =
            selectedHall?.minCapacity != null ? Number(selectedHall.minCapacity) : null;
        const maxCapacity =
            selectedHall?.maxCapacity != null ? Number(selectedHall.maxCapacity) : null;
        if (Number.isFinite(minCapacity) && guests < minCapacity) {
            window.alert(`Capacity must be at least ${minCapacity}.`);
            return null;
        }
        if (Number.isFinite(maxCapacity) && guests > maxCapacity) {
            window.alert(`Capacity must be at most ${maxCapacity}.`);
            return null;
        }

        let nextQuotedTotal;
        if (isEditing && String(quotedTotal || '').trim() !== '') {
            nextQuotedTotal = Number.parseFloat(quotedTotal);
            if (!Number.isFinite(nextQuotedTotal) || nextQuotedTotal < 0) {
                window.alert('Enter a valid quoted total.');
                return null;
            }
        }

        const amenityParts = [
            ...selectedAmenities,
            String(amenityNotes || '').trim(),
        ].filter(Boolean);

        const payload = {
            customerName: String(customerName).trim(),
            customerContact: String(customerContact).replace(/\D/g, '').slice(-10),
            eventDate,
            eventTime: eventTime || undefined,
            eventType: String(eventType || '').trim() || undefined,
            numberOfGuests: guests,
            estimatedTotal: estimatedTotal !== '' ? Number(estimatedTotal) : undefined,
            amenityNotes: amenityParts.length ? amenityParts.join(', ').slice(0, 500) : '',
            specialRequests: String(specialRequests || '').trim() || '',
            partyHallId: partyHallId ? Number(partyHallId) : undefined,
        };

        if (isEditing) {
            payload.updatedBy = Number(actorId);
            payload.quoteStatus = quoteStatus || 'REQUESTED';
            if (nextQuotedTotal != null) {
                payload.quotedTotal = nextQuotedTotal;
            }
        } else {
            payload.restaurantId = Number(restaurantId);
            payload.createdBy = Number(actorId);
            if (!payload.amenityNotes) delete payload.amenityNotes;
            if (!payload.specialRequests) delete payload.specialRequests;
        }

        return payload;
    };

    const handleSubmit = async (e, { confirmOrder = false } = {}) => {
        if (e?.preventDefault) e.preventDefault();

        const payload = buildValidatedPayload();
        if (!payload) return;

        if (isEditing && confirmOrder) {
            const ok = await confirmAction({
                title: 'Confirm this party hall quote?',
                message: 'Confirming will move this quote into Party Hall orders (bookings).',
                confirmLabel: 'Confirm booking',
                cancelLabel: 'Keep editing',
            });
            if (!ok) return;
            payload.quoteStatus = 'CONFIRMED';
        }

        if (isEditing && !confirmOrder && payload.quoteStatus === 'CONFIRMED') {
            window.alert('Use “Confirm booking” to confirm this quote.');
            return;
        }

        setSaving(true);
        try {
            if (isEditing) {
                await updatePartyHallQuote(editQuoteId, payload);
                navigate(
                    confirmOrder
                        ? '/home?tab=orders&orderType=partyHall&partyHallView=orders'
                        : PARTY_HALL_QUOTES_LIST_PATH
                );
            } else {
                await createPartyHallQuote(payload);
                navigate(PARTY_HALL_QUOTES_LIST_PATH);
            }
        } catch (err) {
            window.alert(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    (isEditing
                        ? 'Failed to update party hall quote'
                        : 'Failed to create party hall quote')
            );
        } finally {
            setSaving(false);
        }
    };

    const partyHallEnabled =
        !enabledServiceTypes || enabledServiceTypes.includes('PARTY_HALL');

    const handleSelectOrderChannel = (key) => {
        navigate(`/home?tab=orders&orderType=${key}`);
    };

    const formBusy = saving || loadingHalls || loadingQuote;

    return (
        <RestaurantDashboardLayout
            activeTab="orders"
            orderChannel="partyHall"
            onSelectOrderChannel={handleSelectOrderChannel}
            enabledServiceTypes={enabledServiceTypes}
            servicesLoading={servicesLoading}
        >
            <div className="purchase-detail-page catering-quote-create-page">
                <div className="restaurant-detail-panel">
                    <div className="restaurant-detail-card">
                        <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                            <div className="inventory-item-detail-header-row">
                                <button
                                    type="button"
                                    className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                    onClick={() => navigate(PARTY_HALL_QUOTES_LIST_PATH)}
                                    aria-label="Back to party hall quotes"
                                >
                                    <FaArrowLeft aria-hidden />
                                </button>
                                <h3 className="restaurant-detail-section-title">
                                    {isEditing
                                        ? `Edit Party Hall Quote #${editQuoteId}`
                                        : 'Create Party Hall Quote'}
                                </h3>
                            </div>
                        </div>

                    {!partyHallEnabled ? (
                        <p className="restaurant-detail-message error">
                            Party Hall service is not enabled for this restaurant.
                        </p>
                    ) : resolvingRestaurant || loadingQuote ? (
                        <p className="catering-quote-create-muted">
                            {loadingQuote ? 'Loading quote…' : 'Loading restaurant…'}
                        </p>
                    ) : !restaurantId ? (
                        <p className="restaurant-detail-message error">
                            Logged-in restaurant not found. Select a restaurant from the header or
                            sign in again.
                        </p>
                    ) : (
                        <form
                            className="catering-quote-create-form"
                            onSubmit={(e) => handleSubmit(e, { confirmOrder: false })}
                        >
                            <section className="catering-quote-create-section">
                                <h4 className="catering-quote-create-section-title">Customer</h4>
                                <div className="catering-quote-create-grid">
                                    <label className="catering-quote-create-field">
                                        <span>Name *</span>
                                        <input
                                            type="text"
                                            value={customerName}
                                            onChange={(e) => setCustomerName(e.target.value)}
                                            placeholder="Customer name"
                                            maxLength={200}
                                        />
                                    </label>
                                    <label className="catering-quote-create-field">
                                        <span>Phone *</span>
                                        <input
                                            type="tel"
                                            value={formatMobileDisplay(customerContact)}
                                            onChange={(e) =>
                                                setCustomerContact(sanitizeMobileInput(e.target.value))
                                            }
                                            placeholder="123-456-7890"
                                            inputMode="numeric"
                                        />
                                    </label>
                                </div>
                            </section>

                            <section className="catering-quote-create-section">
                                <h4 className="catering-quote-create-section-title">Hall & Event</h4>
                                <div className="catering-quote-create-grid">
                                    <label className="catering-quote-create-field catering-quote-create-field--wide">
                                        <span>Party hall *</span>
                                        <select
                                            value={partyHallId}
                                            onChange={(e) => handleHallChange(e.target.value)}
                                            disabled={loadingHalls || halls.length === 0}
                                        >
                                            <option value="">
                                                {loadingHalls
                                                    ? 'Loading halls…'
                                                    : halls.length === 0
                                                      ? 'No active hall configured'
                                                      : 'Select hall'}
                                            </option>
                                            {halls.map((hall) => (
                                                <option
                                                    key={hall.partyHallId}
                                                    value={hall.partyHallId}
                                                >
                                                    {formatHallLabel(hall)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="catering-quote-create-field">
                                        <span>
                                            Capacity *
                                            {selectedHall?.minCapacity != null ||
                                            selectedHall?.maxCapacity != null
                                                ? ` (${
                                                      selectedHall.minCapacity != null &&
                                                      selectedHall.maxCapacity != null
                                                          ? `${selectedHall.minCapacity} – ${selectedHall.maxCapacity}`
                                                          : selectedHall.maxCapacity != null
                                                            ? `up to ${selectedHall.maxCapacity}`
                                                            : `min ${selectedHall.minCapacity}`
                                                  })`
                                                : ''}
                                        </span>
                                        <input
                                            type="number"
                                            min={
                                                selectedHall?.minCapacity != null
                                                    ? selectedHall.minCapacity
                                                    : 1
                                            }
                                            max={
                                                selectedHall?.maxCapacity != null
                                                    ? selectedHall.maxCapacity
                                                    : undefined
                                            }
                                            value={numberOfGuests}
                                            onChange={(e) =>
                                                setNumberOfGuests(e.target.value.replace(/\D/g, ''))
                                            }
                                            placeholder={
                                                selectedHall?.minCapacity != null &&
                                                selectedHall?.maxCapacity != null
                                                    ? `Enter ${selectedHall.minCapacity} – ${selectedHall.maxCapacity}`
                                                    : 'Enter capacity'
                                            }
                                        />
                                    </label>
                                    <label className="catering-quote-create-field">
                                        <span>Event date *</span>
                                        <input
                                            type="date"
                                            min={minEventDate}
                                            value={eventDate}
                                            onChange={(e) => setEventDate(e.target.value)}
                                        />
                                    </label>
                                    <label className="catering-quote-create-field">
                                        <span>Event time</span>
                                        <input
                                            type="time"
                                            value={eventTime}
                                            onChange={(e) => setEventTime(e.target.value)}
                                        />
                                    </label>
                                    <label className="catering-quote-create-field">
                                        <span>Event type</span>
                                        <input
                                            type="text"
                                            value={eventType}
                                            onChange={(e) => setEventType(e.target.value)}
                                            placeholder="Wedding, birthday, etc."
                                            maxLength={50}
                                        />
                                    </label>
                                    <label className="catering-quote-create-field">
                                        <span>Estimated total (₹)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={estimatedTotal}
                                            onChange={(e) => setEstimatedTotal(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </label>
                                    {isEditing ? (
                                        <>
                                            <label className="catering-quote-create-field">
                                                <span>Quoted total (₹)</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={quotedTotal}
                                                    onChange={(e) => setQuotedTotal(e.target.value)}
                                                    placeholder="Optional"
                                                />
                                            </label>
                                            <label className="catering-quote-create-field">
                                                <span>Quote status</span>
                                                <select
                                                    value={quoteStatus}
                                                    onChange={(e) => setQuoteStatus(e.target.value)}
                                                >
                                                    {QUOTE_STATUS_EDIT_OPTIONS.map((status) => (
                                                        <option key={status} value={status}>
                                                            {status.replace(/_/g, ' ')}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </>
                                    ) : null}
                                    <div className="catering-quote-create-field catering-quote-create-field--wide">
                                        <span className="catering-quote-create-field-label">
                                            Amenities
                                        </span>
                                        {hallAmenities.length === 0 ? (
                                            <p className="catering-quote-create-muted">
                                                No amenities listed for this hall. Add notes below if
                                                needed.
                                            </p>
                                        ) : (
                                            <div className="party-hall-amenities-checkboxes">
                                                {hallAmenities.map((amenity) => {
                                                    const checked = selectedAmenities.includes(amenity);
                                                    return (
                                                        <label
                                                            key={amenity}
                                                            className="party-hall-amenity-option"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleAmenity(amenity)}
                                                            />
                                                            <span>{amenity}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    <label className="catering-quote-create-field catering-quote-create-field--wide">
                                        <span>Additional amenity notes</span>
                                        <input
                                            type="text"
                                            value={amenityNotes}
                                            onChange={(e) => setAmenityNotes(e.target.value)}
                                            placeholder="Optional notes beyond the checklist"
                                            maxLength={500}
                                        />
                                    </label>
                                    <label className="catering-quote-create-field catering-quote-create-field--wide">
                                        <span>Special requests</span>
                                        <textarea
                                            value={specialRequests}
                                            onChange={(e) => setSpecialRequests(e.target.value)}
                                            rows={3}
                                            placeholder="Optional notes"
                                        />
                                    </label>
                                </div>
                            </section>

                            <div className="catering-quote-create-actions">
                                <button
                                    type="button"
                                    className="restaurant-detail-btn-cancel"
                                    onClick={() => navigate(PARTY_HALL_QUOTES_LIST_PATH)}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                {isEditing ? (
                                    <>
                                        <button
                                            type="submit"
                                            className="restaurant-detail-btn-save"
                                            disabled={formBusy || halls.length === 0}
                                        >
                                            {saving ? 'Saving…' : 'Save quote'}
                                        </button>
                                        <button
                                            type="button"
                                            className="restaurant-detail-btn-save"
                                            disabled={formBusy || halls.length === 0}
                                            onClick={() => handleSubmit(null, { confirmOrder: true })}
                                        >
                                            Confirm booking
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="submit"
                                        className="restaurant-detail-btn-save"
                                        disabled={formBusy || halls.length === 0}
                                    >
                                        {saving ? 'Creating…' : 'Create quote'}
                                    </button>
                                )}
                            </div>
                        </form>
                    )}
                    </div>
                </div>
            </div>
        </RestaurantDashboardLayout>
    );
};

export default CreatePartyHallQuote;
